import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Checkbox, Chip, CircularProgress, Grid, Paper, Typography } from "@mui/material";
import _ from 'lodash';
import moment from 'moment';
import { toast } from 'react-toastify';
import SaveButton from '../../../components/SaveButton';
import WarningCard from '../../../components/publish/WarningCard';
import { MbTextField } from '../../../components/MbTextFields';
import useAppInfo from '../../../hooks/appInfo.hook';
import { mobilizeiStoresService } from '../../../services/mobilizei/stores.service';
import { nuvemshopService } from '../../../services/mobilizei/nuvemshop.service';
import AuthorizationModal from '../../Onboarding/Connect/AuthorizationModal';
import { AUTH_ORIGINS } from '../../../utils/constants';

const FEATURE_ID = 'appOnlyCoupons';
const MESSAGE_MAX_LENGTH = 200;
const ACTIVATION_ERROR_CODE = '1321';
const MISSING_PERMISSION_ERROR_CODE = '1322';

// Stores that installed our app before it asked for the permission to load the
// checkout app have to authorize it again. What the merchant was saving is kept
// until he comes back, so it is saved without him having to select it again.
const PENDING_COUPONS_KEY = 'pending_app_only_coupons';

const getPendingCoupons = () => {
    try {
        return JSON.parse(sessionStorage.getItem(PENDING_COUPONS_KEY));
    } catch (e) {
        return null;
    }
}

const describeDiscount = (coupon) => {
    const value = parseFloat(coupon.value);

    switch (coupon.type) {
        case 'percentage':
            return `${value}% de desconto`;
        case 'absolute':
            return `${value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} de desconto`;
        case 'shipping':
            return 'Frete grátis';
        default:
            return '';
    }
}

const describeCoupon = (coupon) => {
    const parts = [describeDiscount(coupon)];

    if (coupon.end_date) {
        parts.push(`válido até ${moment(coupon.end_date).format('DD/MM/YYYY')}`);
    }

    return parts.filter(part => !_.isEmpty(part)).join(' · ');
}

const sortById = (coupons) => _.sortBy(coupons, 'id');

export default function NuvemshopAppOnlyCoupons() {
    const { appInfo } = useAppInfo();

    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [loadingError, setLoadingError] = useState(false);
    const [authorizationState, setAuthorizationState] = useState('closed');

    // Coupons created on Nuvemshop
    const [storeCoupons, setStoreCoupons] = useState([]);

    // What is saved on the feature: [{ id, code }] and the message
    const [savedCoupons, setSavedCoupons] = useState([]);
    const [savedMessage, setSavedMessage] = useState('');

    const [selectedIds, setSelectedIds] = useState([]);
    const [message, setMessage] = useState('');

    const load = () => {
        setIsLoading(true);
        setLoadingError(false);

        Promise.all([
            nuvemshopService.getAllCoupons(appInfo.storeId, appInfo.accessToken),
            mobilizeiStoresService.getStoreFeatureById(appInfo.platformId, appInfo.storeId, FEATURE_ID)
        ]).then(([coupons, feature]) => {
            const featureCoupons = _.get(feature, 'coupons', []);
            const featureMessage = _.get(feature, 'message', '');

            setStoreCoupons(_.sortBy(coupons, coupon => coupon.code.toUpperCase()));
            setSavedCoupons(featureCoupons);
            setSavedMessage(featureMessage);
            setSelectedIds(featureCoupons.map(coupon => coupon.id));
            setMessage(featureMessage);

            restorePendingCoupons(coupons, featureCoupons);
        }).catch(() => {
            setLoadingError(true);
        }).finally(() => {
            setIsLoading(false);
        })
    }

    // The pending coupons come back selected. When the merchant comes back signed in
    // by the authorization, they are saved right away; otherwise he cancelled it,
    // and they stay selected for him to decide.
    const restorePendingCoupons = (coupons, featureCoupons) => {
        const pending = getPendingCoupons();

        sessionStorage.removeItem(PENDING_COUPONS_KEY);

        if (!pending) return;

        const knownIds = [...coupons, ...featureCoupons].map(coupon => coupon.id);
        const pendingCoupons = _.get(pending, 'coupons', []).filter(coupon => knownIds.includes(coupon.id));
        const pendingMessage = _.get(pending, 'message', '');

        setSelectedIds(pendingCoupons.map(coupon => coupon.id));
        setMessage(pendingMessage);

        const isBackFromAuthorization = new URLSearchParams(window.location.search).has('at');

        if (isBackFromAuthorization && pendingCoupons.length > 0) {
            save({ coupons: pendingCoupons, message: pendingMessage });
        }
    }

    useEffect(() => {
        load();
    }, []);

    // Selected coupons that no longer exist on Nuvemshop. They stay listed so the
    // merchant can unselect them.
    const missingCoupons = useMemo(() => (
        savedCoupons.filter(saved => !storeCoupons.some(coupon => coupon.id === saved.id))
    ), [savedCoupons, storeCoupons]);

    // What would be saved now. The code always comes from Nuvemshop when the coupon
    // still exists there, so a coupon renamed on Nuvemshop gets its code updated.
    const couponsToSave = useMemo(() => (
        selectedIds.map(id => {
            const storeCoupon = storeCoupons.find(coupon => coupon.id === id);
            const savedCoupon = savedCoupons.find(coupon => coupon.id === id);

            return { id, code: storeCoupon ? storeCoupon.code : savedCoupon.code };
        })
    ), [selectedIds, storeCoupons, savedCoupons]);

    const hasRenamedCoupons = savedCoupons.some(saved => {
        const storeCoupon = storeCoupons.find(coupon => coupon.id === saved.id);

        return storeCoupon && storeCoupon.code !== saved.code;
    });

    const isDirty = !_.isEqual(sortById(couponsToSave), sortById(savedCoupons)) || message !== savedMessage;

    const toggleCoupon = (id) => {
        setSelectedIds(selectedIds.includes(id) ? _.without(selectedIds, id) : [...selectedIds, id]);
    }

    const discardChanges = () => {
        setSelectedIds(savedCoupons.map(coupon => coupon.id));
        setMessage(savedMessage);
    }

    const closeAuthorizationModal = () => {
        sessionStorage.removeItem(PENDING_COUPONS_KEY);
        setAuthorizationState('closed');
    }

    const save = (data = { coupons: couponsToSave, message: message.trim() }) => {
        setIsSaving(true);

        mobilizeiStoresService.saveAppOnlyCoupons(appInfo.platformId, appInfo.storeId, data)
            .then(feature => {
                toast.success('Alterações salvas com sucesso!');

                setSavedCoupons(feature.coupons);
                setSavedMessage(feature.message);
                setSelectedIds(feature.coupons.map(coupon => coupon.id));
                setMessage(feature.message);
            })
            .catch(error => {
                const errorCode = _.get(error, 'error.code', '');

                if (errorCode === MISSING_PERMISSION_ERROR_CODE) {
                    sessionStorage.setItem(PENDING_COUPONS_KEY, JSON.stringify(data));
                    setAuthorizationState('open');
                } else if (errorCode === ACTIVATION_ERROR_CODE) {
                    toast.error(`${_.get(error, 'error.message')} (#${errorCode})`);
                } else {
                    toast.error(`Não foi possível salvar as alterações. Por favor, tente novamente. (#${errorCode})`);
                }
            })
            .finally(() => {
                setIsSaving(false);
            })
    }

    const renderSection = (title, description, children) => (
        <Box mt={4}>
            <Typography variant="h6">
                {title}
            </Typography>
            <Typography variant="body2" color="textSecondary" marginBottom={2}>
                {description}
            </Typography>
            {children}
        </Box>
    );

    const renderCoupon = (coupon, { isMissing = false } = {}) => {
        const isSelected = selectedIds.includes(coupon.id);

        return (
            <Grid item xs={12} sm={6} lg={4} key={`coupon-${coupon.id}`}>
                <Paper
                    variant="outlined"
                    onClick={() => !isSaving && toggleCoupon(coupon.id)}
                    sx={{
                        alignItems: 'flex-start',
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        borderWidth: 2,
                        cursor: isSaving ? 'default' : 'pointer',
                        display: 'flex',
                        height: '100%',
                        p: 1.5
                    }}
                >
                    <Checkbox checked={isSelected} disabled={isSaving} sx={{ p: 0.5, mr: 1 }} />

                    <Box flexGrow={1} minWidth={0}>
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            <Typography fontWeight="bold" sx={{ wordBreak: 'break-all' }}>
                                {coupon.code}
                            </Typography>
                            {isMissing && <Chip label="Não encontrado" size="small" color="error" variant="outlined" />}
                            {!isMissing && !coupon.valid && <Chip label="Inativo" size="small" variant="outlined" />}
                        </Box>
                        <Typography variant="body2" color="textSecondary">
                            {isMissing ? 'Este cupom não existe mais na Nuvemshop.' : describeCoupon(coupon)}
                        </Typography>
                    </Box>
                </Paper>
            </Grid>
        )
    }

    if (isLoading) {
        return (
            <Box display='flex' alignItems="center" justifyContent="center" height='100%'>
                <CircularProgress />
            </Box>
        )
    }

    if (loadingError) {
        return (
            <Box display='flex' flexDirection='column' alignItems="center" justifyContent="center" height='100%' textAlign='center'>
                <Typography variant="subtitle1" marginBottom={2}>
                    Não foi possível carregar os cupons da sua loja na Nuvemshop.
                </Typography>
                <Button variant="contained" onClick={() => load()}>
                    Tentar novamente
                </Button>
            </Box>
        )
    }

    return (
        <Box flexDirection="column" flexGrow={1} display='flex' justifyContent='flex-start' sx={{ mb: { xs: 18, md: 14 } }}>
            {renderSection(
                'Mensagem',
                'É exibida no checkout do seu site quando um cupom exlusivo do aplicativo é utilizado.',
                <MbTextField
                    disabled={isSaving}
                    placeholder="Este cupom é exclusivo para compras feitas pelo aplicativo e foi removido do seu pedido."
                    helper={`Opcional. Se ficar em branco, uma mensagem padrão será exibida. Caracteres restantes: ${MESSAGE_MAX_LENGTH - message.length}`}
                    maxLength={MESSAGE_MAX_LENGTH}
                    value={message}
                    onChange={event => setMessage(event.target.value)}
                />
            )}

            {renderSection(
                'Cupons exclusivos do aplicativo',
                'Selecione os cupons criados na Nuvemshop que só poderão ser usados em compras feitas pelo aplicativo.',
                <>
                    {hasRenamedCoupons && (
                        <WarningCard
                            marginY={2}
                            content={<>
                                O código de algum cupom selecionado foi alterado na Nuvemshop. Salve as alterações para que o novo código também seja exclusivo do aplicativo.
                            </>}
                        />
                    )}

                    {storeCoupons.length === 0 && missingCoupons.length === 0 ? (
                        <Typography variant="body1" textAlign='center' marginY={4}>
                            Você ainda não criou nenhum cupom na Nuvemshop. Crie seus cupons no painel da
                            Nuvemshop e volte aqui para escolher quais serão exclusivos do aplicativo.
                        </Typography>
                    ) : (
                        <Grid container spacing={2}>
                            {missingCoupons.map(coupon => renderCoupon(coupon, { isMissing: true }))}
                            {storeCoupons.map(coupon => renderCoupon(coupon))}
                        </Grid>
                    )}
                </>
            )}

            <SaveButton
                show={isDirty}
                onCancel={() => discardChanges()}
                onClick={() => save()}
                isLoading={isSaving}
            />

            <AuthorizationModal
                open={authorizationState !== 'closed'}
                platformId={appInfo.platformId}
                redirecting={authorizationState === 'redirecting'}
                authOrigin={AUTH_ORIGINS.APP_ONLY_COUPONS}
                title="Autorize a Mobilizei novamente"
                description="Os cupons exclusivos precisam de uma nova permissão na sua loja. Você vai para o painel da Nuvemshop para autorizar a Mobilizei e depois voltamos para cá automaticamente para salvar seus cupons."
                permissionNote="A Nuvemshop vai pedir permissão para a Mobilizei instalar o script que remove os cupons exclusivos das compras feitas fora do aplicativo. "
                onRedirecting={() => setAuthorizationState('redirecting')}
                onClose={closeAuthorizationModal}
            />
        </Box>
    )
}
